// Settings page — enumerates every visible registry knob from
// `SHOW SETTINGS` and groups by scope:
//
//   1. Cluster (Raft-replicated, runtime-mutable via `SET CLUSTER name = value`)
//   2. Boot    (CLI flags; restart required on this node to change)
//   3. Node    (env-var only; restart required on this node to change)
//
// Hidden test knobs are filtered out server-side by the registry's
// `visible()` iterator and never reach this page.
//
// Wire format consumed (P1+P2a+P2b1):
//   SHOW SETTINGS columns =
//     key, value, source, default, unit, scope, requires_restart, force_active
//
// `source` is currently the same string as `scope` ("boot"/"cluster"/"node");
// the registry tracks each knob's CLI flag and env-var separately but
// `SHOW SETTINGS` does not yet surface them. The page renders `source` as it
// arrives so any future server-side enrichment shows up automatically.

import { useCallback, useEffect, useState } from 'react';
import { I } from '../components/icons.jsx';
import { KineDB } from '@kinedb/client';

// Knobs the registry lists as Cluster scope but that the generic
// `SET CLUSTER name = value` path can't actually apply yet (no atomic
// mirror — they have a bespoke admin command instead). Render read-only
// in the cluster section with a hint pointing at the dedicated command.
const CLUSTER_NO_GENERIC_SET = {
  'cluster.quorum': 'edit via SET CLUSTER QUORUM = N (admin command)',
};

// One-shot read of `SHOW SETTINGS` with a manual refresh trigger. Returns
// rows already shaped as objects keyed by column name so callers don't have
// to thread `columns` through every level.
function useShowSettings() {
  const [state, setState] = useState({
    entries: null,
    loading: true,
    error: null,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    new KineDB().sql('SHOW SETTINGS').then(r => {
      if (cancelled) return;
      const cols = r.columns ?? [];
      const data = r.data ?? [];
      const entries = data.map(row => {
        const o = {};
        cols.forEach((c, i) => { o[c] = row[i]; });
        return o;
      });
      setState({ entries, loading: false, error: null });
    }).catch(e => {
      if (cancelled) return;
      setState({ entries: null, loading: false, error: e.message || String(e) });
    });
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { ...state, refresh };
}

// SHOW SETTINGS encodes booleans as JSON `true`/`false`. Tolerate both
// the bool and the stringly-typed form so a future server-side change
// (e.g. {"true":"true"}) doesn't quietly break the badge.
function isTruthy(v) {
  return v === true || v === 'true' || v === 1 || v === '1';
}

function RestartBadge({ entry }) {
  if (!isTruthy(entry.requires_restart)) return null;
  return <span className="pill orange settings-restart-pill">restart required</span>;
}

function ForceBadge({ entry }) {
  if (!isTruthy(entry.force_active)) return null;
  return (
    <span
      className="pill orange settings-force-pill"
      title="A KINEDB_FORCE_* env var on this node is overriding the cluster value"
    >
      force override
    </span>
  );
}

// Inline-edit row for Cluster-scope knobs that have a runtime mirror
// (everything in CLUSTER_NO_GENERIC_SET falls back to ReadOnlyRow).
function ClusterRow({ entry, onApplied }) {
  const initial = String(entry.value ?? '');
  const [draft, setDraft] = useState(initial);
  const [applying, setApplying] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind: 'ok' | 'err' | 'muted', text }

  // Re-sync the draft if the live value moves underneath us (e.g. another
  // node ran SET CLUSTER for the same knob). Empty string in the input is
  // preserved if the user is mid-edit — only sync when nothing is in flight.
  useEffect(() => {
    if (!applying) setDraft(String(entry.value ?? ''));
    // Only re-fire when the underlying value changes; not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.value]);

  const apply = async () => {
    if (draft === String(entry.value ?? '')) {
      setFeedback({ kind: 'muted', text: 'no change' });
      return;
    }
    if (draft.trim() === '') {
      setFeedback({ kind: 'err', text: 'enter a value first' });
      return;
    }
    setApplying(true);
    setFeedback(null);
    try {
      // P2a generic surface — registry resolves type and validates server-side.
      await new KineDB().sql(`SET CLUSTER ${entry.key} = ${draft}`);
      setFeedback({ kind: 'ok', text: 'applied via Raft' });
      onApplied?.();
    } catch (e) {
      setFeedback({ kind: 'err', text: e.message || String(e) });
    } finally {
      setApplying(false);
    }
  };

  return (
    <tr>
      <td className="settings-cell-key" title={entry.key}>
        <code>{entry.key}</code>
      </td>
      <td className="settings-cell-edit">
        <div className="settings-edit-group">
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
            disabled={applying}
            spellCheck={false}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={apply}
            disabled={applying}
          >
            <I.Save size={11} /> {applying ? 'applying…' : 'Apply'}
          </button>
        </div>
        {feedback && (
          <div className={`settings-feedback settings-feedback-${feedback.kind}`}>
            {feedback.text}
          </div>
        )}
      </td>
      <td className="settings-cell-default mono">
        {entry.default !== '' && entry.default != null ? <code>{String(entry.default)}</code> : <span className="muted">—</span>}
      </td>
      <td className="settings-cell-unit muted">{entry.unit || ''}</td>
      <td className="settings-cell-source mono">{entry.source}</td>
      <td className="settings-cell-flags"><ForceBadge entry={entry} /></td>
    </tr>
  );
}

// Read-only row used by Boot and Node sections — and by the few Cluster
// knobs that don't have a runtime mirror yet (e.g. cluster.quorum).
function ReadOnlyRow({ entry, hint }) {
  return (
    <tr>
      <td className="settings-cell-key" title={entry.key}>
        <code>{entry.key}</code>
      </td>
      <td className="settings-cell-value mono">
        {entry.value !== '' && entry.value != null ? <code>{String(entry.value)}</code> : <span className="muted">—</span>}
        {hint && <div className="settings-cell-hint muted">{hint}</div>}
      </td>
      <td className="settings-cell-default mono">
        {entry.default !== '' && entry.default != null ? <code>{String(entry.default)}</code> : <span className="muted">—</span>}
      </td>
      <td className="settings-cell-unit muted">{entry.unit || ''}</td>
      <td className="settings-cell-source mono">{entry.source}</td>
      <td className="settings-cell-flags">
        <RestartBadge entry={entry} />
        <ForceBadge entry={entry} />
      </td>
    </tr>
  );
}

function ClusterTable({ entries, onApplied }) {
  if (entries.length === 0) {
    return <div className="settings-empty">No cluster-scope knobs reported.</div>;
  }
  return (
    <table className="k-table settings-table">
      <thead>
        <tr>
          <th style={{ width: '28%' }}>key</th>
          <th style={{ width: '32%' }}>value (editable)</th>
          <th style={{ width: '14%' }}>default</th>
          <th style={{ width: '8%' }}>unit</th>
          <th style={{ width: '10%' }}>source</th>
          <th style={{ width: '8%' }}></th>
        </tr>
      </thead>
      <tbody>
        {entries.map(entry => {
          const noGenericReason = CLUSTER_NO_GENERIC_SET[entry.key];
          if (noGenericReason) {
            return <ReadOnlyRow key={entry.key} entry={entry} hint={noGenericReason} />;
          }
          return <ClusterRow key={entry.key} entry={entry} onApplied={onApplied} />;
        })}
      </tbody>
    </table>
  );
}

function ReadOnlyTable({ entries, emptyText }) {
  if (entries.length === 0) {
    return <div className="settings-empty">{emptyText}</div>;
  }
  return (
    <table className="k-table settings-table">
      <thead>
        <tr>
          <th style={{ width: '28%' }}>key</th>
          <th style={{ width: '32%' }}>value</th>
          <th style={{ width: '14%' }}>default</th>
          <th style={{ width: '8%' }}>unit</th>
          <th style={{ width: '10%' }}>source</th>
          <th style={{ width: '8%' }}></th>
        </tr>
      </thead>
      <tbody>
        {entries.map(entry => <ReadOnlyRow key={entry.key} entry={entry} />)}
      </tbody>
    </table>
  );
}

function ScopeSection({ title, hint, count, children }) {
  return (
    <div className="k-card settings-card">
      <div className="k-card-head">
        <span className="title">{title}</span>
        <span className="muted settings-card-count">{count} knob{count === 1 ? '' : 's'}</span>
      </div>
      {hint && <div className="settings-card-hint">{hint}</div>}
      <div className="settings-card-body">
        {children}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { entries, loading, error, refresh } = useShowSettings();
  const all = entries ?? [];

  // TODO(P3): When the recovery-override env vars (KINEDB_FORCE_QUORUM,
  // KINEDB_FORCE_REPLICATION_FACTOR) ship, fetch GET /api/force-overrides
  // from each node and render a red banner here naming the overridden knobs
  // and the nodes they're active on. Tracked in tasks/cleanup-settings.md.
  const forceBanner = null;

  const cluster = all.filter(e => e.scope === 'cluster');
  const boot    = all.filter(e => e.scope === 'boot');
  const node    = all.filter(e => e.scope === 'node');

  return (
    <div className="page settings-page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Settings</div>
          <h1>Settings</h1>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={refresh}
            disabled={loading}
            title="Re-read SHOW SETTINGS"
          >
            <I.Refresh size={11} /> {loading ? 'reloading…' : 'Reload'}
          </button>
        </div>
      </div>

      <div className="page-body settings-body">
        {forceBanner}

        {error && (
          <div className="k-card settings-error-card">
            <div className="k-card-body">
              <strong>Failed to load SHOW SETTINGS:</strong>
              <pre className="settings-error-body">{error}</pre>
            </div>
          </div>
        )}

        {!error && !loading && all.length === 0 && (
          <div className="settings-empty">SHOW SETTINGS returned no rows.</div>
        )}

        {!error && all.length > 0 && (
          <>
            <ScopeSection
              title="Cluster (runtime-tunable, replicated via Raft)"
              hint="Edit a value and click Apply. The change is proposed through Raft and takes effect on every node on the next read."
              count={cluster.length}
            >
              <ClusterTable entries={cluster} onApplied={refresh} />
            </ScopeSection>

            <ScopeSection
              title="Boot (CLI flags, per-process)"
              hint="Set at process startup via the kinedb CLI flag. Changing one of these requires a restart of THIS node."
              count={boot.length}
            >
              <ReadOnlyTable entries={boot} emptyText="No boot-scope knobs reported." />
            </ScopeSection>

            <ScopeSection
              title="Node (env-var, per-process)"
              hint="Per-node tuning via the environment variable bound at process startup. Changing one of these requires a restart of THIS node."
              count={node.length}
            >
              <ReadOnlyTable entries={node} emptyText="No node-scope knobs reported." />
            </ScopeSection>
          </>
        )}
      </div>
    </div>
  );
}
